/**
 * Sync Status Modal
 * 
 * Displays detailed sync status including pending changes, conflicts, and sync history.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  View,
  ScrollView,
  Pressable,
  ActivityIndicator,
  StyleSheet,
  useWindowDimensions,
  Alert,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { getSyncStatus, syncOfflineData, forceSync, subscribeToSyncStatus, type SyncStatus } from '@/services/offlineSync';
import { syncQueue, type DeadLetterSyncAction, type SyncAction } from '@/services/syncQueue';
import { formatDistanceToNow } from 'date-fns';
import { describeSyncAction } from '@/utils/syncActionDescriptions';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getDismissA11yProps } from '@/utils/accessibility';

type SyncStatusModalProps = {
  visible: boolean;
  onClose: () => void;
};

const SYNC_NOW_DEBOUNCE_MS = 1200;

export function SyncStatusModal({ visible, onClose }: SyncStatusModalProps) {
  const theme = useTheme();
  const { height: windowHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clearingDeadLetter, setClearingDeadLetter] = useState(false);
  const [retryingDeadLetter, setRetryingDeadLetter] = useState(false);
  const [syncNowDebounced, setSyncNowDebounced] = useState(false);
  const [queuedActions, setQueuedActions] = useState<SyncAction[]>([]);
  const [deadLetterActions, setDeadLetterActions] = useState<DeadLetterSyncAction[]>([]);
  const syncNowDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!visible) return;

    // Load initial status
    const loadStatus = async () => {
      setLoading(true);
      try {
        const status = await getSyncStatus();
        setSyncStatus(status);
        const [queued, dead] = await Promise.all([
          syncQueue.getQueuedActions(),
          syncQueue.getDeadLetterActions(),
        ]);
        setQueuedActions(queued);
        setDeadLetterActions(dead);
      } catch (error) {
        console.error('[SyncStatusModal] Error loading sync status:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStatus();

    // Subscribe to status changes
    const unsubscribe = subscribeToSyncStatus((status) => {
      setSyncStatus(status);
      setIsSyncing(status.isSyncing);
    });

    const refreshQueue = () => {
      void Promise.all([syncQueue.getQueuedActions(), syncQueue.getDeadLetterActions()])
        .then(([queued, dead]) => {
          setQueuedActions(queued);
          setDeadLetterActions(dead);
        })
        .catch(() => {});
    };
    const unsubscribeQueue = syncQueue.subscribeToQueueChanges(refreshQueue);

    return () => {
      if (syncNowDebounceTimerRef.current) {
        clearTimeout(syncNowDebounceTimerRef.current);
        syncNowDebounceTimerRef.current = null;
      }
      unsubscribe();
      unsubscribeQueue();
    };
  }, [visible]);

  const handleForceSync = async () => {
    if (syncNowDebounced || isSyncing || syncStatus?.isSyncing) {
      return;
    }
    setSyncNowDebounced(true);
    if (syncNowDebounceTimerRef.current) {
      clearTimeout(syncNowDebounceTimerRef.current);
    }
    syncNowDebounceTimerRef.current = setTimeout(() => {
      setSyncNowDebounced(false);
      syncNowDebounceTimerRef.current = null;
    }, SYNC_NOW_DEBOUNCE_MS);
    setIsSyncing(true);
    try {
      await forceSync();
      const status = await getSyncStatus();
      setSyncStatus(status);
      setQueuedActions(await syncQueue.getQueuedActions());
      setDeadLetterActions(await syncQueue.getDeadLetterActions());
    } catch (error) {
      console.error('[SyncStatusModal] Error forcing sync:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleClearDeadLetter = () => {
    Alert.alert(
      'Clear sync error log?',
      "This hides the banner and removes saved error details on this device. Your edits stay as they are. If something still didn't upload, fix or re-add it in the app (e.g. Wallet or Plan), then use Sync Now.",
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Clear log',
          style: 'destructive',
          onPress: () => {
            void (async () => {
              setClearingDeadLetter(true);
              try {
                await syncQueue.clearDeadLetterQueue();
                setSyncStatus(await getSyncStatus());
                setDeadLetterActions(await syncQueue.getDeadLetterActions());
              } catch (error) {
                console.error('[SyncStatusModal] Error clearing dead-letter queue:', error);
              } finally {
                setClearingDeadLetter(false);
              }
            })();
          },
        },
      ],
    );
  };

  const handleRetryDeadLetter = async () => {
    if (retryingDeadLetter || deadLetterFailureCount === 0) return;
    setRetryingDeadLetter(true);
    try {
      await syncQueue.retryAllDeadLetterActions();
      const status = await getSyncStatus();
      setSyncStatus(status);
      const [queued, dead] = await Promise.all([
        syncQueue.getQueuedActions(),
        syncQueue.getDeadLetterActions(),
      ]);
      setQueuedActions(queued);
      setDeadLetterActions(dead);
      if (status.isOnline) {
        await syncOfflineData();
      }
    } catch (error) {
      console.error('[SyncStatusModal] Error retrying dead-letter queue:', error);
    } finally {
      setRetryingDeadLetter(false);
    }
  };

  const formatLastSync = (date: Date): string => {
    if (date.getTime() === 0) return 'Never';
    try {
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const deadLetterFailureCount = useMemo(
    () => Math.max(syncStatus?.failedChanges ?? 0, deadLetterActions.length),
    [syncStatus?.failedChanges, deadLetterActions.length],
  );
  const pendingChangeCount = useMemo(
    () => Math.max(syncStatus?.pendingChanges ?? 0, queuedActions.length),
    [syncStatus?.pendingChanges, queuedActions.length],
  );
  const isSyncingUi = isSyncing || Boolean(syncStatus?.isSyncing);
  const showSyncNowButton = Boolean(syncStatus?.isOnline);
  const canPressSyncNow = showSyncNowButton && !isSyncingUi && !syncNowDebounced;

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
      presentationStyle="pageSheet"
    >
      <View style={[styles.overlay, { backgroundColor: theme.colors.modalOverlay }]}>
        <Pressable
          style={styles.backdrop}
          onPress={onClose}
          {...getDismissA11yProps('sync status modal')}
        />
        <View
          style={[
            styles.content,
            {
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: theme.radii.xl,
              borderTopRightRadius: theme.radii.xl,
              maxHeight: windowHeight * 0.88,
              minHeight: Math.min(windowHeight * 0.52, 420),
            },
          ]}
        >
          {/* Header */}
          <View
            style={[
              styles.header,
              {
                borderBottomColor: theme.colors.borderMuted,
              },
            ]}
          >
            <View style={styles.headerLeft}>
              <View
                style={[
                  styles.iconContainer,
                  { backgroundColor: theme.colors.primary + '15' },
                ]}
              >
                <Ionicons name="sync" size={24} color={theme.colors.primary} />
              </View>
              <SubHeading>Sync Status</SubHeading>
            </View>
            <Pressable
              onPress={onClose}
              style={styles.closeButton}
              accessibilityRole="button"
              accessibilityLabel="Close sync status"
              accessibilityHint="Closes sync status details"
            >
              <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
            </Pressable>
          </View>

          {/* Content */}
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={theme.colors.primary} />
                <Body color={theme.colors.textSecondary} style={styles.loadingText}>
                  Loading sync status...
                </Body>
              </View>
            ) : syncStatus ? (
              <>
                {/* Connection Status */}
                <Card style={styles.section}>
                  <View style={styles.statusRow}>
                    <View style={styles.statusLeft}>
                      <Ionicons
                        name={syncStatus.isOnline ? 'cloud-done' : 'cloud-offline'}
                        size={24}
                        color={syncStatus.isOnline ? theme.colors.success : theme.colors.error}
                      />
                      <View style={styles.statusText}>
                        <Body variant="bodyStrong">
                          {syncStatus.isOnline ? 'Online' : 'Offline'}
                        </Body>
                        <Caption color={theme.colors.textSecondary}>
                          {syncStatus.isOnline
                            ? 'Connected to the internet'
                            : 'No internet connection'}
                        </Caption>
                      </View>
                    </View>
                    {syncStatus.isOnline && (
                      <View
                        style={[
                          styles.statusBadge,
                          { backgroundColor: theme.colors.success + '15' },
                        ]}
                      >
                        <Ionicons name="checkmark-circle" size={16} color={theme.colors.success} />
                      </View>
                    )}
                  </View>
                </Card>

                {/* Last Sync */}
                <Card style={styles.section}>
                  <View style={styles.infoRow}>
                    <Ionicons
                      name="time-outline"
                      size={20}
                      color={theme.colors.textSecondary}
                    />
                    <View style={styles.infoText}>
                      <Caption color={theme.colors.textSecondary}>Last Synced</Caption>
                      <Body variant="bodyStrong">
                        {formatLastSync(syncStatus.lastSync)}
                      </Body>
                    </View>
                  </View>
                </Card>

                {/* Pending Changes */}
                {pendingChangeCount > 0 && (
                  <Card
                    style={[
                      styles.section,
                      { borderLeftWidth: 3, borderLeftColor: theme.colors.warning },
                    ]}
                  >
                    <View style={styles.infoRow}>
                      <Ionicons
                        name="hourglass-outline"
                        size={20}
                        color={theme.colors.warning}
                      />
                      <View style={styles.infoText}>
                        <Caption color={theme.colors.textSecondary}>Pending Changes</Caption>
                        <Body variant="bodyStrong">
                          {pendingChangeCount} action{pendingChangeCount !== 1 ? 's' : ''} waiting to sync
                        </Body>
                      </View>
                    </View>
                    {queuedActions.length > 0 ? (
                      <View style={{ marginTop: 12, gap: 8 }}>
                        <Caption color={theme.colors.textSecondary}>Queued (first in line first)</Caption>
                        {queuedActions.map((a) => (
                          <View
                            key={a.id}
                            style={{
                              paddingVertical: 8,
                              paddingHorizontal: 10,
                              borderRadius: theme.radii.md,
                              backgroundColor: theme.colors.background,
                            }}
                          >
                            <Body variant="bodyStrong" style={{ fontSize: 15 }}>
                              {describeSyncAction(a)}
                            </Body>
                            <Caption color={theme.colors.textSecondary} style={{ marginTop: 2 }}>
                              {formatDistanceToNow(new Date(a.timestamp), { addSuffix: true })}
                            </Caption>
                          </View>
                        ))}
                      </View>
                    ) : null}
                  </Card>
                )}

                {/* Failed Changes (dead letter — not the same as offline) */}
                {(deadLetterFailureCount > 0) && (
                  <Card
                    style={[
                      styles.section,
                      { borderLeftWidth: 3, borderLeftColor: theme.colors.warning },
                    ]}
                  >
                    <View style={styles.infoRow}>
                      <Ionicons
                        name="alert-circle-outline"
                        size={20}
                        color={theme.colors.warning}
                      />
                      <View style={styles.infoText}>
                        <Caption color={theme.colors.textSecondary}>Couldn&apos;t upload</Caption>
                        <Body variant="bodyStrong" color={theme.colors.onWarningContainer}>
                          {deadLetterFailureCount === 1
                            ? 'One change stopped retrying after several failed attempts.'
                            : `${deadLetterFailureCount} changes stopped retrying after several failed attempts.`}
                        </Body>
                        <Caption color={theme.colors.textSecondary} style={{ marginTop: 6 }}>
                          Try Sync Now. If the problem persists, open the relevant trip and re-save or re-add the item. You can clear this log below to dismiss the banner.
                        </Caption>
                      </View>
                    </View>
                    {deadLetterActions.length > 0 ? (
                      <View style={{ marginTop: 12, gap: 8 }}>
                        <Caption color={theme.colors.textSecondary}>Error log (most recent last)</Caption>
                        {deadLetterActions.map((a) => {
                          const reason =
                            a.reason.length > 220 ? `${a.reason.slice(0, 217)}…` : a.reason;
                          return (
                            <View
                              key={`${a.id}-${a.failedAt}`}
                              style={{
                                paddingVertical: 8,
                                paddingHorizontal: 10,
                                borderRadius: theme.radii.md,
                                backgroundColor: theme.colors.background,
                              }}
                            >
                              <Body variant="bodyStrong" style={{ fontSize: 15 }}>
                                {describeSyncAction(a)}
                              </Body>
                              <Caption color={theme.colors.error} style={{ marginTop: 4 }}>
                                {reason}
                              </Caption>
                              <Caption color={theme.colors.textSecondary} style={{ marginTop: 2 }}>
                                {formatDistanceToNow(new Date(a.failedAt), { addSuffix: true })}
                              </Caption>
                            </View>
                          );
                        })}
                      </View>
                    ) : null}
                  </Card>
                )}

                {/* Conflicts */}
                {syncStatus.conflicts > 0 && (
                  <Card
                    style={[
                      styles.section,
                      { borderLeftWidth: 3, borderLeftColor: theme.colors.error },
                    ]}
                  >
                    <View style={styles.infoRow}>
                      <Ionicons
                        name="warning-outline"
                        size={20}
                        color={theme.colors.error}
                      />
                      <View style={styles.infoText}>
                        <Caption color={theme.colors.textSecondary}>Conflicts</Caption>
                        <Body variant="bodyStrong" color={theme.colors.error}>
                          {syncStatus.conflicts} conflict{syncStatus.conflicts !== 1 ? 's' : ''} need resolution
                        </Body>
                      </View>
                    </View>
                  </Card>
                )}

                {/* Sync Progress */}
                {syncStatus.isSyncing && syncStatus.syncProgress && (
                  <Card style={styles.section}>
                    <View style={styles.infoRow}>
                      <ActivityIndicator size="small" color={theme.colors.primary} />
                      <View style={styles.infoText}>
                        <Caption color={theme.colors.textSecondary}>Syncing...</Caption>
                        <Body variant="bodyStrong">
                          {syncStatus.syncProgress.completed} of {syncStatus.syncProgress.total} completed
                        </Body>
                      </View>
                    </View>
                  </Card>
                )}

                {/* Actions */}
                <View style={styles.actions}>
                  {deadLetterFailureCount > 0 && (
                    <Button
                      label={retryingDeadLetter ? 'Retrying…' : 'Retry failed changes'}
                      variant="secondary"
                      onPress={handleRetryDeadLetter}
                      disabled={retryingDeadLetter || clearingDeadLetter}
                      style={styles.actionButton}
                    />
                  )}
                  {showSyncNowButton && (
                    <Button
                      label={isSyncingUi || syncNowDebounced ? 'Syncing…' : 'Sync Now'}
                      onPress={handleForceSync}
                      icon={
                        isSyncingUi || syncNowDebounced ? (
                          <ActivityIndicator size="small" color={theme.colors.onPrimary} />
                        ) : (
                          <Ionicons name="sync" size={18} color={theme.colors.onPrimary} />
                        )
                      }
                      disabled={!canPressSyncNow}
                      style={styles.actionButton}
                    />
                  )}
                  {deadLetterFailureCount > 0 && !isSyncingUi && (
                    <Button
                      label={clearingDeadLetter ? 'Clearing…' : 'Clear error log'}
                      variant="secondary"
                      onPress={handleClearDeadLetter}
                      disabled={clearingDeadLetter || retryingDeadLetter}
                      style={styles.actionButton}
                    />
                  )}
                  {isSyncingUi && !showSyncNowButton && (
                    <View style={styles.syncingContainer}>
                      <ActivityIndicator size="small" color={theme.colors.primary} />
                      <Body color={theme.colors.textSecondary} style={styles.syncingText}>
                        Syncing...
                      </Body>
                    </View>
                  )}
                </View>
              </>
            ) : (
              <View style={styles.errorContainer}>
                <Ionicons name="alert-circle-outline" size={48} color={theme.colors.error} />
                <Body color={theme.colors.textSecondary} style={styles.errorText}>
                  Failed to load sync status
                </Body>
              </View>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    flex: 1,
  },
  content: {},
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    minWidth: 44,
    minHeight: 44,
    padding: 10,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollView: {
    flexGrow: 1,
    flexShrink: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 12,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 12,
  },
  loadingText: {
    marginTop: 8,
  },
  section: {
    padding: 16,
    gap: 8,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  statusText: {
    flex: 1,
    gap: 4,
  },
  statusBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  infoText: {
    flex: 1,
    gap: 4,
  },
  actions: {
    marginTop: 8,
    gap: 12,
  },
  actionButton: {
    width: '100%',
  },
  syncingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
  },
  syncingText: {
    marginLeft: 8,
  },
  errorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 12,
  },
  errorText: {
    marginTop: 8,
    textAlign: 'center',
  },
});

